(() => {
  'use strict';

  // Zonder de markt-prefix haalt de verlanglijst de producten van de
  // standaardmarkt op: Engelse titels en bedragen in de verkeerde valuta, ook
  // als de klant op /nl-nl staat.
  const wortel = () =>
    ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');

  const KEY = 'bellascena_wishlist_v1';
  const EVT = 'bellascena:wishlist:change';

  // Een lege vertaling is een geldige vertaling, dus geen Engelse terugval.
  const plural = (key, count) => (window.bcPlural ? window.bcPlural(key, count) : '');

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {}
    document.dispatchEvent(new CustomEvent(EVT, { detail: { list } }));
  }

  function has(handle) {
    return read().includes(handle);
  }

  function add(handle) {
    if (!handle) return;
    const list = read();
    if (!list.includes(handle)) {
      list.push(handle);
      write(list);
    }
  }

  function remove(handle) {
    if (!handle) return;
    write(read().filter((h) => h !== handle));
  }

  function toggle(handle) {
    if (!handle) return;
    has(handle) ? remove(handle) : add(handle);
  }

  window.BellascenaWishlist = { read, has, add, remove, toggle };

  function syncToggles() {
    const list = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      const saved = list.includes(handle);
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  function syncCount() {
    const count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      if (count === 0) {
        el.setAttribute('hidden', '');
      } else {
        el.removeAttribute('hidden');
      }
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(handle);
  });

  document.addEventListener(EVT, () => {
    syncToggles();
    syncCount();
    renderWishlistPage();
  });

  window.addEventListener('storage', (e) => {
    if (e.key === KEY) {
      syncToggles();
      syncCount();
      renderWishlistPage();
    }
  });

  const money = (cents) => {
    const fmt =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: fmt,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  function renderCard(p, index) {
    const img = p.featured_image || (p.images && p.images[0]) || '';
    const onSale = p.compare_at_price && p.compare_at_price > p.price;

    return `
      <div class="bc-card">
        <a href="${wortel()}products/${p.handle}" class="bc-card__link">
          <div class="bc-card__media">
            ${
              img
                ? `<img src="${img}" class="bc-card__img bc-card__img--primary" alt="${escapeHtml(
                    p.title
                  )}" loading="lazy">`
                : ''
            }
            ${onSale ? '<span class="bc-card__flag">' + escapeHtml(window.bcLabel ? window.bcLabel('sale') : '') + '</span>' : ''}
          </div>
          <div class="bc-card__plaque">
            <span class="bc-card__plaque-ornament" aria-hidden="true"><i></i></span>
            <h3 class="bc-card__name">${escapeHtml(p.title)}</h3>
            <span class="bc-card__price ${onSale ? 'bc-card__price--sale' : ''}">
              <span>${money(p.price)}</span>
              ${onSale ? `<span class="bc-card__price-compare">${money(p.compare_at_price)}</span>` : ''}
            </span>
          </div>
        </a>
        <button type="button" class="bc-card__wishlist is-saved" data-wishlist-toggle data-product-handle="${p.handle}" aria-pressed="true" aria-label="${escapeHtml(window.bcLabel ? window.bcLabel('remove_from_wishlist') : '')}">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 21s-7-4.5-9-8.5C1 8 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 23 8 21 12.5 19 16.5 12 21 12 21z"/></svg>
        </button>
      </div>
    `;
  }

  let renderInFlight = false;

  async function renderWishlistPage() {
    const root = document.querySelector('[data-wishlist-grid]');
    if (!root || renderInFlight) return;
    renderInFlight = true;

    const empty = document.querySelector('[data-wishlist-empty]');
    const count = document.querySelector('[data-wishlist-page-count]');
    const list = read();

    if (list.length === 0) {
      root.innerHTML = '';
      root.setAttribute('hidden', '');
      empty && empty.removeAttribute('hidden');
      count && (count.textContent = plural('wishlist_saved', 0));
      renderInFlight = false;
      return;
    }

    empty && empty.setAttribute('hidden', '');
    root.removeAttribute('hidden');

    const results = await Promise.all(
      list.map(async (handle) => {
        try {
          const res = await fetch(`${wortel()}products/${encodeURIComponent(handle)}.js`);
          if (!res.ok) return null;
          return await res.json();
        } catch {
          return null;
        }
      })
    );

    const valid = results.filter(Boolean);

    // Clean up any handles that 404'd (product was deleted)
    if (valid.length !== list.length) {
      const validHandles = valid.map((p) => p.handle);
      write(list.filter((h) => validHandles.includes(h)));
    }

    root.innerHTML = valid.map((p, i) => renderCard(p, i)).join('');
    count && (count.textContent = plural('wishlist_saved', valid.length));
    syncToggles();
    renderInFlight = false;
  }

  function init() {
    syncToggles();
    syncCount();
    renderWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
